import api from '../utils/api';
import { DischargeSummary } from '../types/dischargeSummary';

export const DischargeSummaryService = {
  async getAll(): Promise<DischargeSummary[]> {
    const response = await api.get('/discharge-summaries');
    return response.data;
  },

  async getByHorseId(horseId: string): Promise<DischargeSummary[]> {
    const response = await api.get('/discharge-summaries', { params: { horseId } });
    return response.data;
  },

  async getById(id: string): Promise<DischargeSummary> {
    const response = await api.get(`/discharge-summaries/${id}`);
    return response.data;
  },

  async create(data: Omit<DischargeSummary, 'id' | 'horse' | 'createdAt' | 'updatedAt' | 'clinicName' | 'clinicPhone' | 'clinicEmail' | 'clinicAddress'>): Promise<DischargeSummary> {
    const response = await api.post('/discharge-summaries', data);
    return response.data;
  },

  async update(id: string, data: Partial<Omit<DischargeSummary, 'id' | 'horseId' | 'horse' | 'createdAt' | 'updatedAt' | 'clinicName' | 'clinicPhone' | 'clinicEmail' | 'clinicAddress'>>): Promise<DischargeSummary> {
    const response = await api.put(`/discharge-summaries/${id}`, data);
    return response.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/discharge-summaries/${id}`);
  },

  async finalize(id: string): Promise<DischargeSummary> {
    const response = await api.post(`/discharge-summaries/${id}/finalize`);
    return response.data;
  },

  async getDraftByHorseId(horseId: string): Promise<DischargeSummary | null> {
    const response = await api.get(`/discharge-summaries/draft/horse/${horseId}`);
    return response.data;
  },

  async getSuggestions(horseId: string): Promise<{
    soapNotes: any[];
    medicalRecords: any[];
    procedures: any[];
    vaccinations: any[];
    medications: any[];
  }> {
    const response = await api.get(`/discharge-summaries/suggestions/${horseId}`);
    return response.data;
  },

  async generateWithAI(data: {
    horseId: string;
    selectedSoapNoteIds: string[];
    selectedMedicalRecordIds: string[];
    selectedProcedureIds: string[];
    selectedVaccinationIds: string[];
    selectedMedicationIds: string[];
    admissionDateTime: string;
  }): Promise<DischargeSummary> {
    const response = await api.post('/discharge-summaries/generate', data);
    return response.data;
  },

  async linkRecords(summaryId: string, recordIds: {
    soapNoteIds?: string[];
    medicalRecordIds?: string[];
    procedureIds?: string[];
    vaccinationIds?: string[];
    medicationIds?: string[];
  }): Promise<DischargeSummary> {
    const response = await api.post(`/discharge-summaries/${summaryId}/link-records`, recordIds);
    return response.data;
  },

  async updateDraft(id: string, data: Partial<Omit<DischargeSummary, 'id' | 'horseId' | 'horse' | 'createdAt' | 'updatedAt' | 'clinicName' | 'clinicPhone' | 'clinicEmail' | 'clinicAddress'>>): Promise<DischargeSummary> {
    // Use the existing update method for drafts
    return this.update(id, data);
  },

  // ───── Imaging study attachments ────────────────────────────────────

  async listStudies(summaryId: string): Promise<any[]> {
    const response = await api.get(`/discharge-summaries/${summaryId}/studies`);
    return response.data;
  },

  async attachStudies(
    summaryId: string,
    studies: Array<{
      studyId: string;
      accessionNumber: string;
      includeThumbnails?: boolean;
      includeViewerLink?: boolean;
      includeReportLink?: boolean;
    }>,
  ): Promise<any[]> {
    const response = await api.post(`/discharge-summaries/${summaryId}/studies`, { studies });
    return response.data;
  },

  async detachStudy(summaryId: string, studyId: string): Promise<any[]> {
    const response = await api.delete(`/discharge-summaries/${summaryId}/studies/${studyId}`);
    return response.data;
  },
};